import React from 'react';
import { Card, Avatar, Spin, Alert, Button, Tooltip } from 'antd';
import { UserOutlined, EditOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuthUser, useIsAdmin } from '@/hooks/useAuth';
import { useSpaceAccess } from '@/contexts/SpaceAccessContext';
import { GET_USER, GetUserQueryResponse, GetUserQueryVariables } from './Users';
import { AUTH_API_URL } from './SpaceAssignments';

export const MyProfile: React.FC = () => {
  const { user: auth0User } = useAuth0();
  const { isSuperUser } = useAuthUser();
  const { isAuthApiOwner, currentUserId } = useSpaceAccess();
  const isAdmin = useIsAdmin();

  const {
    data: userData,
    loading: isLoading,
    error,
    refetch,
  } = useQuery<GetUserQueryResponse, GetUserQueryVariables>(GET_USER, {
    variables: { id: currentUserId || '' },
    context: {
      uri: AUTH_API_URL,
    },
    skip: !currentUserId,
    errorPolicy: 'all',
  });

  const currentUser = userData?.user || null;

  // We have Auth0 user data as fallback, so only show error if both fail
  const hasAnyUserData = currentUser || auth0User;

  if (isLoading) {
    return (
      <div className="bg-surface-sunken w-full min-h-screen flex items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  if (error && !hasAnyUserData) {
    return (
      <div className="bg-surface-sunken w-full min-h-screen p-6">
        <Alert
          message="Error loading profile"
          description="Failed to fetch your profile information. Please try again."
          type="error"
          showIcon
          action={
            <Button size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        />
      </div>
    );
  }

  // Fallback to Auth0 user data if the user doesn't exist in the DB
  const displayName = currentUser?.name || auth0User?.name || auth0User?.nickname || '-';
  const displayEmail = currentUser?.email || auth0User?.email || '-';
  // Display role with full hierarchy: Superuser > Owner > Admin > Member
  const displayRole = isSuperUser
    ? 'Superuser'
    : isAuthApiOwner
    ? 'Owner'
    : isAdmin
    ? 'Admin'
    : 'Member';

  return (
    <div className="bg-surface-sunken w-full min-h-screen p-6">
      <Card>
        <div className="flex items-center justify-between mb-6">
          <div className="font-semibold text-base">Basic Information</div>
          <Tooltip title="Coming soon">
            <Button icon={<EditOutlined />} disabled>
              Edit
            </Button>
          </Tooltip>
        </div>

        <div className="mb-6">
          <Avatar
            size={64}
            icon={<UserOutlined />}
            style={{ backgroundColor: 'var(--color-interactive-active-subtle)' }}
          />
        </div>

        <div className="grid grid-cols-2 gap-y-4">
          <div>
            <div className="text-content-tertiary text-sm mb-1">User Name:</div>
            <div className="text-sm">{displayName}</div>
          </div>
          <div>
            <div className="text-content-tertiary text-sm mb-1">Organization:</div>
            <div className="text-sm">Butlr</div>
          </div>
          <div>
            <div className="text-content-tertiary text-sm mb-1">Email:</div>
            <div className="text-sm">{displayEmail}</div>
          </div>
          <div>
            <div className="text-content-tertiary text-sm mb-1">Role:</div>
            <div className="text-sm">{displayRole}</div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default MyProfile;
